import { createFileRoute } from "@tanstack/react-router";
import { physicianPageSchema, safeJsonLd } from "@/lib/structured-data-core";
import { lazy, Suspense, useState } from "react";
import { User, GraduationCap } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { SocialPosts } from "@/components/site/SocialPosts";

import hero from "@/assets/restore/juboori-hero.webp.asset.json";
import { absoluteUrl } from "@/lib/absolute-url";

const Results = lazy(() => import("@/components/site/Results").then(m => ({ default: m.Results })));
import { QuoteBand } from "@/components/site/QuoteBand";
const Reviews = lazy(() => import("@/components/site/Reviews").then(m => ({ default: m.Reviews })));
const QuoteForm = lazy(() => import("@/components/site/QuoteForm").then(m => ({ default: m.QuoteForm })));

export const Route = createFileRoute("/doctors/dr-fahad-juboori")({
  head: () => ({
    links: [{ rel: "canonical", href: "https://restorehair.com/doctors/dr-fahad-juboori/" }],
    meta: [
      { title: "Dr. Fahad Juboori - Hair Transplant Doctor & Surgeon" },
      {
        name: "description",
        content:
          "Dr. Fahad Juboori is a RESTORE Hair Doctor in the Chicagoland area. He is trained by Dr. James Harris and is one of the leading surgeons.",
      },
      { property: "og:title", content: "Dr. Fahad Juboori — Hair Restoration Physician" },
      {
        property: "og:description",
        content:
          "Meet Dr. Fahad Juboori, a distinguished RESTORE® Hair physician dedicated to helping patients regain their confidence.",
      },
      { property: "og:image", content: absoluteUrl(hero.url) },
      { name: "twitter:image", content: absoluteUrl(hero.url) },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: safeJsonLd(
          physicianPageSchema({
            name: "Dr. Fahad Juboori",
            jobTitle: "Hair Restoration Physician",
            description: "Dr. Fahad Juboori is a board-certified internal medicine physician and RESTORE® Hair elite surgical team member specializing in hair restoration.",
            image: hero.url,
            pageUrl: "https://restorehair.com/doctors/dr-fahad-juboori/",
            clinic: {
              street: "4514 Cole Avenue, Office #605",
              city: "Dallas",
              state: "TX",
            },
          }),
        ),
      },
    ],
  }),
  component: DrJuboorPage,
});

function DrJuboorPage() {
  const [tab, setTab] = useState<"bio" | "education">("bio");

  return (
    <div className="min-h-screen bg-background font-sans">
      <Header />
      <main data-cta-location="doctor">
        {/* Hero */}
        <section className="overflow-hidden bg-background">
          <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-4 pt-10 lg:grid-cols-2 lg:items-end lg:gap-4">
            <div className="pb-10 lg:pb-16">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground">
                Dr. Fahad Juboori
              </h1>
              <p className="mt-3 text-lg md:text-xl font-semibold text-primary">
                Hair Restoration Physician
              </p>
              <p className="mt-6 text-base md:text-lg leading-relaxed text-muted-foreground">
                Raised in Baltimore, Maryland, Dr. Fahad Juboori has devoted his career to
                caring for his patients. Before specializing in hair restoration, Dr. Juboori
                was a seasoned hospitalist physician, caring for over 15,000 patients and
                performing a wide range of procedures.
              </p>
            </div>
            <div className="flex justify-center lg:justify-end lg:self-end">
              <img
                src={hero.url}
                alt="Dr. Fahad Juboori — Hair Restoration Physician"
                className="w-full max-w-lg object-contain"
                loading="eager"
              />
            </div>
          </div>
        </section>
        <QuoteBand />

        {/* Bio / Education tabs */}
        <section className="bg-muted/40 py-12 md:py-16">
          <div className="mx-auto w-full max-w-6xl px-4">
            <div className="grid max-w-3xl grid-cols-2 gap-1">
              <button
                type="button"
                onClick={() => setTab("bio")}
                className={`inline-flex items-center justify-center gap-2 px-6 py-5 text-lg font-semibold transition ${
                  tab === "bio"
                    ? "bg-primary text-primary-foreground"
                    : "bg-card text-foreground shadow-sm hover:bg-card/80"
                }`}
              >
                <User size={20} />
                Full Bio
              </button>
              <button
                type="button"
                onClick={() => setTab("education")}
                className={`inline-flex items-center justify-center gap-2 px-6 py-5 text-lg font-semibold transition ${
                  tab === "education"
                    ? "bg-primary text-primary-foreground"
                    : "bg-card text-foreground shadow-sm hover:bg-card/80"
                }`}
              >
                <GraduationCap size={20} />
                Education
              </button>
            </div>

            <div className="bg-card px-4 py-10 md:px-10 md:py-14 shadow-sm">
              <div hidden={tab !== "bio"}>
                <div className="mx-auto max-w-6xl">
                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold text-foreground">
                      Dr. Fahad Juboori's Bio
                    </h2>
                    <div className="mt-4 space-y-4 text-base leading-relaxed text-muted-foreground">
                      <p>
                        Raised in Baltimore, Maryland, Dr. Fahad Juboori has devoted his
                        career to caring for his patients. Before specializing in hair
                        restoration, Dr. Juboori was a seasoned hospitalist physician, he has
                        cared for over 15,000 patients, performing a wide range of procedures.
                      </p>
                      <p>
                        Dr. Juboori's journey to hair restoration is personal. Having
                        experienced hair loss himself and witnessed its effects within his
                        family, he chose to specialize in this field to help others facing
                        similar challenges. Chosen personally by Dr. Harris and highly
                        respected by his RESTORE peers, Dr. Juboori plays a key role in the
                        RESTORE® Hair elite surgical team. He is dedicated to helping his
                        patients regain their self-esteem and empowering them to thrive in
                        both their personal and professional lives.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div hidden={tab !== "education"}>
                <div className="mx-auto max-w-6xl">
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground">
                    Education &amp; Speciality
                  </h2>
                  <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-3">
                    <div className="rounded-md border border-border p-6">
                      <p className="text-sm uppercase tracking-wide text-primary">
                        Education
                      </p>
                      <p className="mt-2 text-lg font-semibold text-foreground">2015</p>
                      <p className="mt-2 text-sm text-muted-foreground">
                        St George's University School of Medicine
                      </p>
                    </div>
                    <div className="rounded-md border border-border p-6">
                      <p className="text-sm uppercase tracking-wide text-primary">
                        Residency
                      </p>
                      <p className="mt-2 text-lg font-semibold text-foreground">2018</p>
                      <p className="mt-2 text-sm text-muted-foreground">
                        New York Presbyterian Brooklyn Methodist Hospital
                      </p>
                    </div>
                    <div className="rounded-md border border-border p-6">
                      <p className="text-sm uppercase tracking-wide text-primary">
                        Board Certification
                      </p>
                      <p className="mt-2 text-lg font-semibold text-foreground">
                        Internal Medicine
                      </p>
                      <p className="mt-2 text-sm text-muted-foreground">
                        American Board of Internal Medicine (2018)
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <Suspense fallback={null}>
          <Results resultsHref="https://restorehair.com/results/" resultsButtonLabel="See More Results" />
          <Reviews />
          <SocialPosts />
          <QuoteForm />
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}